import { VehicleType } from '@repo/shared'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableCell,
  TableRow,
} from '@repo/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { Container, Edit, Eye, MoreVertical, Trash } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDeleteVehicle } from '../../hooks/use-delete-vehicle'

import type { VehicleWithTrailer } from '@repo/shared'

import { ConfirmDeleteDialog } from '@/components/shared/confirm-delete-dialog'
import { VehicleStatusBadge } from '@/components/shared/vehicle-status-badge'

type Props = {
  trailer: VehicleWithTrailer
}

export function TrailerTableRow({ trailer }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutate: deleteVehicle } = useDeleteVehicle()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const handleViewDetails = () => {
    navigate({
      to: '/dashboard/vehicles/$vehicleId',
      params: { vehicleId: String(trailer.id) },
      search: { type: VehicleType.Trailer },
    })
  }

  const handleDelete = () => {
    deleteVehicle(Number(trailer.id))
    setIsDeleteOpen(false)
  }

  const dimensions = `${trailer.trailer.length}m x ${trailer.trailer.width}m x ${trailer.trailer.height}m`

  return (
    <>
      <TableRow
        className="bg-background hover:bg-muted/50 cursor-pointer"
        onClick={handleViewDetails}
      >
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="bg-background flex size-9 items-center justify-center rounded-xs border">
              <Container className="text-primary size-4" />
            </div>
            <div>
              <p className="font-medium">{trailer.licensePlate}</p>
              <p className="text-muted-foreground text-xs">
                {trailer.vinNumber}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell>{trailer.manufacturer}</TableCell>
        <TableCell>
          {t(`vehicles:addTrailerForm.trailerTypes.${trailer.trailer.type}`)}
        </TableCell>
        <TableCell>
          <VehicleStatusBadge status={trailer.status} />
        </TableCell>
        <TableCell>{dimensions}</TableCell>
        <TableCell>{(trailer.trailer.maxWeight / 1000).toFixed(1)}t</TableCell>
        <TableCell>
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" className="bg-card">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleViewDetails}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('common:viewDetails')}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/dashboard/vehicles/$vehicleId/edit"
                    params={{ vehicleId: trailer.id.toString() }}
                    search={{ type: VehicleType.Trailer }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    {t('common:edit')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  {t('common:delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>

      <ConfirmDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDelete}
        title={t('vehicles:deleteTrailer.title')}
        description={t('vehicles:deleteTrailer.description')}
        cancelLabel={t('vehicles:deleteTrailer.cancel')}
        confirmLabel={t('vehicles:deleteTrailer.confirm')}
      />


    </>
  )
}
